import { Badge, Flag, Icons, isFlagCode, Text } from '@mnd-frontend/ui/publish/components';
import { useLocation } from '@mnd-frontend/ui/publish/contexts';
import { media, withLineClamp } from '@mnd-frontend/ui/publish/utils';
import {
  GREY_200,
  GREY_400,
  GREY_800,
  GREY_900,
  NEUTRAL_WHITE,
  RED_100,
  RED_600,
  RED_700,
  SHADOW_1,
  SPACING_1,
  SPACING_1_5,
  SPACING_2,
  SPACING_3,
} from '@mnd-frontend/ui/shared/tokens';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { matchPath } from 'react-router';
import styled, { css } from 'styled-components';
import { track } from '../../../../../lib/tracker';
import DebugInfo from '../../DebugInfo';
import type { MainMenuItem } from '../types';
import NavigationLink from './NavigationLink';

// #region Styled components
const SubList = styled.ul<{
  $isBottom: boolean;
  $offset: number;
  $show: boolean;
  $hoverGroup: boolean;
  $subNavOffset?: number;
}>`
  position: fixed;
  visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  z-index: 101;
  left: ${SPACING_1};
  top: calc(var(--header-height-mobile) - 6px);
  ${media.greaterThan('navigationBreakpoint')} {
    top: unset;
    bottom: unset;
    left: ${({ $subNavOffset }) => ($subNavOffset ? $subNavOffset + 'px' : 'var(--mnd-navigation-width)')};
    ${({ $isBottom, $offset }) => ($isBottom ? `bottom: ${$offset}px` : `top: ${$offset}px`)};
  }
  background: ${NEUTRAL_WHITE};
  border-radius: ${SPACING_2};
  list-style: none;
  padding: ${({ $hoverGroup }) => ($hoverGroup ? '0' : `${SPACING_1_5} 0`)};
  margin: 0;
  width: ${({ $hoverGroup }) => ($hoverGroup ? '417px' : '270px')};
  border: 1px solid ${GREY_400};
  box-shadow: ${SHADOW_1};
  max-height: 85vh;
  overflow-y: auto;
  display: grid;
  grid-auto-rows: auto;
  gap: ${({ $hoverGroup }) => ($hoverGroup ? '0' : SPACING_1)};

  button {
    background: none;
    border: none;
    padding: ${SPACING_1} ${SPACING_3};
    font-size: 0.875rem;
  }

  hr {
    border: 0;
    border-top: 1px solid;
    background: ${GREY_400};
    color: ${GREY_400};
  }

  h3 {
    padding: ${SPACING_1_5} ${SPACING_2} 0 ${SPACING_2};
    font-weight: 700;
    color: ${GREY_900};
  }
`;

const PressroomListItem = styled.li<{ $active?: boolean }>`
  height: var(--navigation-large-button-size);
  * {
    max-height: 100%;
  }
  a {
    height: 100%;
    color: ${({ $active }) => ($active ? RED_700 : GREY_900)};
    width: 92%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    padding: 0 ${SPACING_2};
    justify-content: space-between;
    border-radius: ${SPACING_1};
    background-color: ${({ $active }) => ($active ? RED_100 : 'transparent')};
    > span {
      width: 100px;
      flex: 1;
      ${withLineClamp(2)}
      word-break: break-word;
    }
    > i {
      flex-shrink: 0;
    }
    &:focus-visible {
      outline: 0;
      background-color: ${GREY_200};
    }
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${({ $active }) => ($active ? RED_100 : GREY_200)};
      }
    }
  }
`;

export const SubListItem = styled.li<{ $hoverGroup: boolean; $active: boolean }>`
  min-height: 53px;
  position: relative;
  &.sub-list-heading {
    height: auto;
    min-height: unset;
    padding: ${SPACING_1_5} ${SPACING_2} 0 ${SPACING_2};
    span {
      font-weight: 700;
      color: ${GREY_900};
    }
  }
  & button {
    display: block;
    font-weight: 500;
    width: 92%;
    height: 100%;
    padding: 0 ${SPACING_2};
    margin: 0 auto;
    text-align: left;
    border-radius: ${SPACING_1};
    &:focus-visible {
      outline: 0;
      background: ${GREY_200};
    }
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${GREY_200};
      }
    }
  }
  & a {
    height: 100%;
    display: flex;
    color: ${({ $active }) => ($active ? RED_700 : GREY_900)};
    ${({ $hoverGroup }) =>
      $hoverGroup
        ? css`
            width: 100%;
            padding: ${SPACING_2} ${SPACING_3};
            gap: ${SPACING_2};
            align-items: center;
          `
        : css`
            width: 92%;
            margin: 0 auto;
            display: flex;
            align-items: center;
            padding: 0 ${SPACING_2};
            border-radius: ${SPACING_1};
            .to-right {
              margin-left: auto;
            }
          `}

    i {
      flex-shrink: 0;
    }

    &:focus-visible {
      outline: 0;
      background: ${GREY_200};
    }
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${GREY_200};
        color: ${({ $active }) => ($active ? RED_700 : GREY_900)};
      }
    }
  }
`;

const TextColumn = styled.div`
  flex: 1;
  font-size: 0.875rem;
  line-height: 1.3125rem;
`;

const TopText = styled.div`
  font-weight: 500;
  &:only-child {
    font-size: 1rem;
  }
`;

const DescriptionText = styled.div<{ $active: boolean }>`
  color: ${props => (props.$active ? RED_600 : GREY_800)};
  font-size: 0.75rem;
  line-height: 0.875rem;
`;

const ActiveIndicator = styled.div`
  position: absolute;
  width: 6px;
  height: 60%;
  top: 20%;
  background: ${RED_700};
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`;
// #endregion

interface SubMenuProps {
  itemData: MainMenuItem;
  offsetTop: number;
  showSubNavOnHover: boolean;
  topGroup: boolean;
  show: boolean;
  pressroomSelector: boolean;
  ariaId: string;
  ariaLabelledby: string;
  siteCode?: string;
  close: () => void;
  subNavOffset?: number;
}

const SubMenu = ({
  itemData,
  offsetTop,
  showSubNavOnHover,
  topGroup,
  show,
  subNavOffset,
  pressroomSelector,
  ariaId,
  ariaLabelledby,
  siteCode,
  close,
}: SubMenuProps) => {
  const ref = useRef<HTMLUListElement>(null);
  const location = useLocation();
  const [bottomOffset, setBottomOffset] = useState<null | number>(null);

  useEffect(() => {
    if (!show && ref.current) {
      ref.current.scrollTop = 0;
    }
  }, [show]);

  useLayoutEffect(() => {
    if (ref.current && show) {
      const { height } = ref.current.getBoundingClientRect();
      if (offsetTop + height > window.innerHeight) {
        // 68 is height of button (56) + 12 for design offset
        setBottomOffset(
          Math.min(window.innerHeight - offsetTop - 68, window.innerHeight - height - 12),
        );
      } else {
        setBottomOffset(null);
      }
    }
  }, [show, offsetTop]);

  let offset = offsetTop;
  if (bottomOffset !== null) {
    offset = bottomOffset;
  } else if (topGroup && !pressroomSelector) {
    offset -= 12;
  }

  return (
    <SubList
      ref={ref}
      $isBottom={bottomOffset !== null}
      $offset={offset}
      $show={show}
      $subNavOffset={subNavOffset}
      id={ariaId}
      aria-labelledby={ariaLabelledby}
      $hoverGroup={showSubNavOnHover}
    >
      {pressroomSelector ? (
        <>
          <Text as="h3" variant="labelSmall">
            {itemData.heading}
          </Text>

          {itemData.items.map(item => (
            <PressroomListItem
              key={item.siteCode}
              $active={item.siteCode === siteCode}
              onClick={() =>
                track({
                  event: 'Navigation link: clicked',
                  traits: { menuCategory: itemData.menuState, linkText: item.text },
                })
              }
            >
              <a href={item.url} title={item.text}>
                <Text as="span" variant="labelMedium">
                  {item.text}
                </Text>
                {item.siteCode && isFlagCode(item.siteCode) && (
                  <Flag code={item.siteCode} size="24px" />
                )}
              </a>
            </PressroomListItem>
          ))}
        </>
      ) : (
        <>
          {itemData.items.map(item => {
            const isPublications = item.text === 'Publications';
            const active = topGroup
              ? isPublications
                ? Array.isArray(item.activeUrl)
                  ? !!item.activeUrl.find(url => matchPath(url, location.pathname))
                  : !!matchPath(item.activeUrl ?? '', location.pathname)
                : item.url === location.pathname
              : false;

            return (
              <Fragment key={`${itemData.menuState}-${item.url}`}>
                {item.separator && (
                  <li>
                    <hr />
                  </li>
                )}
                <SubListItem $hoverGroup={showSubNavOnHover} $active={active}>
                  {active && <ActiveIndicator aria-hidden="true" />}
                  <NavigationLink
                    href={item.url}
                    activeUrl={item.activeUrl}
                    newTab={item.newTab}
                    onClick={() => {
                      track({
                        event: 'Navigation link: clicked',
                        traits: { menuCategory: itemData.menuState, linkText: item.text },
                      });
                      close();
                    }}
                  >
                    {item.icon}
                    <TextColumn>
                      <TopText>{item.text}</TopText>
                      {item?.description && (
                        <DescriptionText $active={active}>{item.description}</DescriptionText>
                      )}
                    </TextColumn>
                    {item?.description && item.badge && (
                      <Badge variant={item.badge.variant} text={item.badge.text} />
                    )}
                    {item.newTab && <Icons.ExternalLink $size="xs" className="to-right" />}
                  </NavigationLink>
                </SubListItem>
              </Fragment>
            );
          })}
          {itemData.addDebugInfo && (
            <SubListItem $hoverGroup={showSubNavOnHover} $active={false}>
              <DebugInfo fixedPosition={false} />
            </SubListItem>
          )}
        </>
      )}
    </SubList>
  );
};

export default SubMenu;
